import React, { useEffect, useState } from "react";
import { api, LOGO_URL } from "../../lib/api";
import { toast } from "sonner";

const DEFAULTS = {
  thanks_email_enabled: "true",
  thanks_email_subject: "",
  thanks_email_intro: "",
};

export default function AdminThanksEmail() {
  const [s, setS] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testName, setTestName] = useState("Camille Dupont");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then(r => setS(prev => ({ ...prev, ...r.data })))
      .finally(() => setLoading(false));
  }, []);

  const enabled = String(s.thanks_email_enabled).toLowerCase() === "true";

  const onSave = async () => {
    try {
      setSaving(true);
      const { data } = await api.put("/admin/settings", {
        thanks_email_enabled: enabled ? "true" : "false",
        thanks_email_subject: s.thanks_email_subject,
        thanks_email_intro: s.thanks_email_intro,
      });
      setS(prev => ({ ...prev, ...data }));
      toast.success("Email de remerciement enregistré");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onTest = async (e) => {
    e?.preventDefault?.();
    if (!testEmail) return;
    try {
      setTesting(true);
      // Send current form values (possibly unsaved) so the test reflects
      // what the admin sees in the preview, not the persisted version.
      const { data } = await api.post("/admin/thanks-email/test", {
        to: testEmail,
        subject: s.thanks_email_subject || undefined,
        intro: s.thanks_email_intro || undefined,
        customer_name: testName || undefined,
      });
      toast.success(`Email envoyé à ${data.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "L'envoi a échoué");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="p-8 text-berulia-inkmuted">Chargement…</div>;

  // Sample values used to materialize the `{prenom}` / `{nom_complet}`
  // placeholders in the preview. Backend uses real order data at send-time.
  const SAMPLE = { prenom: "Camille", nom_complet: "Camille Dupont" };
  const interpolate = (tpl) =>
    (tpl || "").replace(/\{([A-Za-z_]+)\}/g, (m, k) => {
      const key = k.toLowerCase();
      return Object.prototype.hasOwnProperty.call(SAMPLE, key) ? SAMPLE[key] : m;
    });

  const introParagraphs = interpolate(s.thanks_email_intro || "")
    .split("\n\n")
    .map(p => p.trim())
    .filter(Boolean);
  const previewSubject = interpolate(s.thanks_email_subject || "");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl">
      <h1 className="font-heading text-3xl text-berulia-inkdeep mb-2">Email de remerciement</h1>
      <p className="text-berulia-inkmuted text-sm mb-8">
        Envoyé automatiquement après chaque commande payée, en plus de la confirmation. Personnalise le sujet et le message ci-dessous.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-6 bg-white border border-berulia-border p-6 rounded">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => setS(p => ({ ...p, thanks_email_enabled: e.target.checked ? "true" : "false" }))}
              data-testid="thanks-enabled"
            />
            <span className="text-sm text-berulia-inkdeep">Activer l'envoi automatique</span>
          </label>

          <div>
            <label className="block overline text-berulia-inkdeep mb-2">Sujet de l'email</label>
            <input
              type="text"
              value={s.thanks_email_subject}
              onChange={e => setS(p => ({ ...p, thanks_email_subject: e.target.value }))}
              placeholder="BERULIA — Un mot pour vous remercier ✿"
              className="w-full border border-berulia-border px-3 py-2 text-sm"
              data-testid="thanks-subject"
            />
          </div>

          <div>
            <label className="block overline text-berulia-inkdeep mb-2">Message</label>
            <p className="text-xs text-berulia-inkmuted mb-2">
              Séparer les paragraphes par une ligne vide. Personnaliser avec{" "}
              <code className="px-1 py-0.5 bg-berulia-beige text-berulia-inkdeep">{"{prenom}"}</code>
              {" "}ou{" "}
              <code className="px-1 py-0.5 bg-berulia-beige text-berulia-inkdeep">{"{nom_complet}"}</code>.
            </p>
            <textarea
              value={s.thanks_email_intro}
              onChange={e => setS(p => ({ ...p, thanks_email_intro: e.target.value }))}
              rows={10}
              className="w-full border border-berulia-border px-3 py-2 text-sm font-sans"
              data-testid="thanks-intro"
            />
          </div>

          <div className="pt-4 border-t border-berulia-border">
            <button
              onClick={onSave}
              disabled={saving}
              data-testid="thanks-save"
              className="btn-romance disabled:opacity-50">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>

          <form onSubmit={onTest} className="pt-4 border-t border-berulia-border space-y-3">
            <p className="overline text-berulia-inkdeep">Tester l'envoi</p>
            <p className="text-xs text-berulia-inkmuted">
              Envoie un email réel à l'adresse indiquée avec le sujet et le message
              actuellement saisis (même non enregistrés).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="email"
                required
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="vous@exemple.com"
                className="border border-berulia-border px-3 py-2 text-sm"
                data-testid="thanks-test-email"
              />
              <input
                type="text"
                value={testName}
                onChange={e => setTestName(e.target.value)}
                placeholder="Prénom Nom (pour {prenom})"
                className="border border-berulia-border px-3 py-2 text-sm"
                data-testid="thanks-test-name"
              />
            </div>
            <button
              type="submit"
              disabled={testing || !testEmail}
              data-testid="thanks-test-send"
              className="border border-berulia-inkdeep text-berulia-inkdeep px-4 py-2 text-sm hover:bg-berulia-beige disabled:opacity-50 transition">
              {testing ? "Envoi…" : "Envoyer un test"}
            </button>
          </form>
        </div>

        {/* Preview */}
        <div className="bg-berulia-beige border border-berulia-border p-6 rounded">
          <p className="overline text-berulia-rosedeep mb-2">Aperçu</p>
          <p className="text-xs text-berulia-inkmuted mb-4">
            Exemple avec une cliente fictive « {SAMPLE.nom_complet} ».
          </p>
          {previewSubject && (
            <div className="mb-4 text-xs text-berulia-inkmuted">
              <span className="overline">Objet :</span>{" "}
              <span className="text-berulia-inkdeep">{previewSubject}</span>
            </div>
          )}
          <div style={{ fontFamily: "Georgia, serif", background: "#F7F4F0", padding: 24, color: "#2C2623" }}>
            <div style={{ maxWidth: 480, margin: "auto", background: "#fff", padding: 32, border: "1px solid #EAE3DB" }}>
              <div style={{ textAlign: "center", margin: "0 0 8px" }}>
                <img src={LOGO_URL} alt="BERULIA" style={{ height: 40, width: "auto", display: "inline-block" }}/>
              </div>
              <p style={{ textAlign: "center", fontStyle: "italic", color: "#9b8d85", margin: "4px 0 24px", fontSize: 13 }}>
                prêt-à-porter féminin
              </p>
              {introParagraphs.map((p, i) => (
                <p key={i} style={{ lineHeight: 1.8 }}>{p}</p>
              ))}
              <p style={{ marginTop: 24, color: "#9b8d85", fontSize: 12, textAlign: "center" }}>
                Avec amour, l'équipe BERULIA
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
